import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import apiService from '../services/api';
import { readAdminSessionUser } from '../utils/settingsSessionUser.js';
import {
  buildNr2jOwnerFilterOptions,
  nr2jCanOpenMember,
  nr2jOwnerFilterParams,
  nr2jRowOwner,
  nr2jShowsOwnerColumn,
  nr2jViewerKind,
} from '../utils/nr2jOwnerScope.js';

/**
 * Filtre « Commercial » partagé (Prospection, Clients, Tâches, Devis).
 * Valeur initiale : ?owner=… (liens depuis Mon équipe).
 * @param {object[]} [rows] lignes chargées — complètent les options (propriétaires hors annuaire).
 */
export function useNr2jOwnerFilter(rows = []) {
  const [searchParams] = useSearchParams();
  const [session] = useState(() => readAdminSessionUser());
  const kind = nr2jViewerKind(session);
  const showOwner = nr2jShowsOwnerColumn(kind);
  const canOpenMember = nr2jCanOpenMember(session);
  const viewerId = session?.id || null;

  const [value, setValue] = useState(() => (showOwner ? searchParams.get('owner') || '' : ''));
  const [members, setMembers] = useState([]);
  const seenOwners = useRef(new Map());
  const [seenVersion, setSeenVersion] = useState(0);

  useEffect(() => {
    if (!showOwner) return undefined;
    let cancelled = false;
    const load = kind === 'manager'
      ? apiService.listNr2jMyTeam().then((res) => res?.members || [])
      : apiService.listNr2jTeam().then((res) => res?.members || []);
    load
      .then((list) => {
        if (!cancelled) setMembers(list);
      })
      .catch(() => {
        if (!cancelled) setMembers([]);
      });
    return () => {
      cancelled = true;
    };
  }, [kind, showOwner]);

  useEffect(() => {
    if (!showOwner || kind !== 'admin') return;
    let added = false;
    for (const row of rows) {
      const owner = nr2jRowOwner(row);
      if (owner?.id && !seenOwners.current.has(owner.id)) {
        seenOwners.current.set(owner.id, owner);
        added = true;
      }
    }
    if (added) setSeenVersion((n) => n + 1);
  }, [rows, showOwner, kind]);

  const options = useMemo(
    () => buildNr2jOwnerFilterOptions(kind, {
      members,
      viewerId,
      extraOwners: [...seenOwners.current.values()],
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- seenVersion invalide le ref
    [kind, members, viewerId, seenVersion],
  );

  const params = useMemo(() => nr2jOwnerFilterParams(value), [value]);

  return {
    showOwner,
    canOpenMember,
    viewerId,
    value,
    setValue,
    options,
    params,
  };
}
